import { ServiceTypeEnum } from '@secca/shared/models/enums';
import { DocumentTemplateFilter } from './documentTemplateFilter';
import { DocumentTemplate } from 'src/app/shared/models/document-template';
import { DocumentService } from 'src/app/core/services/document.service';
import { TranslateService } from '@ngx-translate/core';
import { SubGopDetails } from '../../models/subGopDetails';
import { Component, OnInit, Input } from '@angular/core';
import { AccountGopService } from '@secca/core/services/account-gop.service';
import { AccountGopInfo } from '@secca/shared/models/account-gop-info';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '@secca/shared/decorators/auto-unsubscribe';

@Component({
    selector: 'app-sub-gop-details',
    templateUrl: './sub-gop-details.component.html',
    styleUrls: ['./sub-gop-details.component.scss'],
    standalone: false
})
@AutoUnsubscribe
export class SubGopDetailsComponent implements OnInit {
  @Input() serviceTypeEnum: ServiceTypeEnum;
  @Input() subGopDetails: SubGopDetails;
  @Input() templateFilter: DocumentTemplateFilter;
  @Input() hideItemsFields = false;

  itemsInGopPlaceholder: string;
  itemsNotInGopPlaceholder: string;

  fullTemplateList: DocumentTemplate[];
  templateNames = [];
  templateLanguages = [];
  templateToLanguagesMap: { [templateName: string]: string[] } = {};

  accountGopInfo: AccountGopInfo | null = null;

  $postTemplatesByTypeSubscr: Subscription;
  $gopInfoSubscr: Subscription;

  constructor(private translateService: TranslateService,
              private documentService: DocumentService,
              private accountGopService: AccountGopService) {}

  ngOnInit() {
    this.initSubscribers();
    this.initPlaceholders();
  }

  initPlaceholders(){
    if (this.serviceTypeEnum === ServiceTypeEnum.ACCOMMODATION) {
      this.itemsInGopPlaceholder = this.translateService.instant('sub-gop-details-items-in-gop-placeholder-accommodation');
      this.itemsNotInGopPlaceholder = this.translateService.instant('sub-gop-details-items-not-in-gop-placeholder-accommodation');
    }
    if (this.serviceTypeEnum === ServiceTypeEnum.TREATMENT) {
      this.itemsInGopPlaceholder = this.translateService.instant('sub-gop-details-items-in-gop-placeholder-treatment');
      this.itemsNotInGopPlaceholder = this.translateService.instant('sub-gop-details-items-not-in-gop-placeholder-treatment');
    }
  }

  initSubscribers() {
    this.$postTemplatesByTypeSubscr = this.documentService.postTemplatesByType(this.templateFilter).subscribe({
      next: (gopTemplates) => {
        this.fullTemplateList = gopTemplates;
        this.mapToTemplateNameWithLanguagesList(gopTemplates);
        this.accountGopInfoChanged();
      }
    });

    this.$gopInfoSubscr = this.accountGopService.getAccountGopInfo().subscribe({
      next: (gop) => {
        this.accountGopInfo = gop;
        this.accountGopInfoChanged();
      },
      error: (error) => console.error('Error fetching accountGopInfo:', error)
    });
  }

  resetSubGobDetails(){
    this.subGopDetails.templateName = '';
    this.subGopDetails.templateLanguage = '';
    this.subGopDetails.templateId = '';
  }

  accountGopInfoChanged() {
    if (!this.accountGopInfo || !this.fullTemplateList || this.fullTemplateList.length === 0) {
      return;
    }
    if (this.subGopDetails.templateName) {
      // If templateName is already set, we must not change it because the user has already selected it.
      // Make sure that user can change language if needed - otherwise stucked to the saved language.
      this.templateLanguages = this.templateToLanguagesMap[this.subGopDetails.templateName] || [];
      return;
    }
    this.resetSubGobDetails();
    const templateName = this.findTemplateName(this.templateNames, this.accountGopInfo.gopTemplate);
    if (!templateName) {
      return;
    }
    this.subGopDetails.templateName = templateName;
    this.templateSelected(templateName);

    const langCode = this.translateToLanguageCode(this.accountGopInfo.gopLanguage);
    const lang = this.templateToLanguagesMap[templateName].find(x => x === langCode);
    if (lang) {
      this.subGopDetails.templateLanguage = lang;
    } else {
      this.subGopDetails.templateLanguage = 'EN';
    }
    this.updateSelectedTemplateId();
  }

  findTemplateName(templateNames: string[], gopTemplate: string) {
    const exact = templateNames.find(x => x === gopTemplate);
    if (exact) {
      return exact;
    }
    return templateNames.find(x => x.startsWith(gopTemplate));
  }

  templateSelected(selectedTemplateName: string) {
    if (selectedTemplateName) {
      this.templateLanguages = this.templateToLanguagesMap[selectedTemplateName];
    }
    if (this.templateLanguages.length === 1) {
      this.subGopDetails.templateLanguage = this.templateLanguages[0];
    }
    this.updateSelectedTemplateId();
  }

  updateSelectedTemplateId() {
    const gopTemplate = this.fullTemplateList.find(
      gopTemplateResult =>
        gopTemplateResult.templateLanguage === this.subGopDetails.templateLanguage &&
        gopTemplateResult.templateName === this.subGopDetails.templateName
    );
    this.subGopDetails.templateId = gopTemplate ? gopTemplate.templateId : '';
  }

  mapToTemplateNameWithLanguagesList(gopTemplates: DocumentTemplate[]) {
    const templateNameSet = new Set<string>();
    gopTemplates.forEach(gopTemplate => {
      if (this.templateToLanguagesMap[gopTemplate.templateName] == null) {
        this.templateToLanguagesMap[gopTemplate.templateName] = [];
      }
      this.templateToLanguagesMap[gopTemplate.templateName].push(gopTemplate.templateLanguage);
      templateNameSet.add(gopTemplate.templateName);
    });
    this.templateNames = this.sortDocuments(templateNameSet);
  }

  sortDocuments(templateNameSet: Set<string>) {
    const sortedTemplateNames = new Set();
    const gopTemplateNames: SortDocument[] = [];
    const vobTemplateNames: SortDocument[] = [];
    const otherTemplateNames: SortDocument[] = [];
    templateNameSet.forEach(template => {
      const name = template.split(' ', 2);
      if (name[0] === 'GOP') {
        gopTemplateNames.push(new SortDocument({ name1: name[0], name2: name[1], fullName: template } ));
      } else if (name[0] === 'VOB') {
        vobTemplateNames.push(new SortDocument({ name1: name[0], name2: name[1], fullName: template } ));
      } else {
        otherTemplateNames.push(new SortDocument({ name1: name[0], name2: name[1], fullName: template } ));
      }
    });
    gopTemplateNames.sort((a, b) => (a.name2 < b.name2 ? -1 : 1 )).forEach(template => sortedTemplateNames.add(template.fullName));
    vobTemplateNames.sort((a, b) => (a.name2 < b.name2 ? -1 : 1 )).forEach(template => sortedTemplateNames.add(template.fullName));
    otherTemplateNames.sort((a, b) => (a.name1 < b.name1 ? -1 : 1 )).forEach(template => sortedTemplateNames.add(template.fullName));
    return Array.from(sortedTemplateNames);
  }

  translateToLanguageCode(gopLanguage: string): string {
    switch (gopLanguage) {
      case 'Danish':    return 'DA';
      case 'German':    return 'DE';
      case 'English':   return 'EN';
      case 'Spanish':   return 'ES';
      case 'Finnish':   return 'FI';
      case 'French':    return 'FR';
      case 'Icelandic': return 'IS';
      case 'Norwegian': return 'NO';
      case 'Swedish':   return 'SV';
      default:          return '';
    }
  }
}
export class SortDocument {
  name1: string;
  name2: string;
  fullName: string;

  public constructor(init?: Partial<SortDocument>) {
    Object.assign(this, init);
  }
}
